import React, { forwardRef, useEffect, useCallback, useState, useMemo } from 'react';

import { Box, Popover, PopoverBody, PopoverContent, PopoverTrigger, useDisclosure } from '@chakra-ui/react';
import { GenericInputControl } from 'components/RabbitOKRSidePanel/Components/ParentOKRComponent';
import { useCustomTagsQuery } from 'components/RabbitOKRSidePanel/datahooks/useGetCustomTagsOrganization';
import {
  openOKRAddEditCustomTagsPanel,
  SECONDARY_PANEL_TYPES,
} from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import { useCustomTagPermissions } from 'components/hooks/useCustomTagPrivileges';
import { useToast } from 'components/hooks/useToast';
import useVisualViewportHeight from 'components/hooks/useVisualViewportHeight';
// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';

import { Plus } from 'assets/assetsV1/iconComponents';

import { TOAST_STATUSES } from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';

import { Button } from 'uiToolkitV1/Button';

import CustomTagChipList from './CustomTagChipList';
// eslint-disable-next-line import/no-cycle
import TagSearchAndSelect from './TagSearchAndSelect';

const ControlComponent = forwardRef((props, ref) => {
  const {
    isOpen,
    onToggle,
    tags,
    isReadOnly,
    okrId,
    onChange,
    handleTagsSave,
    customTagsData,
    tagNameLengthLimit = 8,
  } = props;

  const dispatch = useDispatch();

  const handleAddTagClick = () => {
    if (customTagsData && customTagsData.length > 0) {
      onToggle();
    } else {
      dispatch(openOKRAddEditCustomTagsPanel({ okrId, onSave: handleTagsSave }));
    }
  };

  return (
    <Box ref={ref}>
      {tags.length > 0 ? (
        <GenericInputControl onClick={onToggle} w="100%" $isActive={isOpen}>
          <Box w="100%">
            <CustomTagChipList
              tags={tags}
              tagsLimit={isMobileView() ? 1 : 3}
              tagNameLengthLimit={tagNameLengthLimit}
              isShowCloseButton={!isReadOnly}
              onTagClose={(tag) => {
                onChange(tags.filter((cur) => cur.uuid !== tag?.uuid));
              }}
            />
          </Box>
        </GenericInputControl>
      ) : (
        <Button variant="tertiary" StartIcon={Plus} color="blue" onClick={handleAddTagClick} disabled={isReadOnly}>
          Add Tag
        </Button>
      )}
    </Box>
  );
});

export const OKRTags = ({
  onChange = () => {},
  isReadOnly = false,
  tags = [],
  okrId = null,
  containerProps = {},
  showNewTagCreation,
  isTagEditionAsChild = false,
  tagNameLengthLimit = 8,
  isTagEditionInSidePanelChildCreation = false,
  isSendUpdatedTagsToParent = false,
}) => {
  const { isOpen, onToggle, onClose } = useDisclosure();
  const [popupWidth, setPopupWidth] = useState(0);
  const [popupOffset, setOffset] = useState(0);
  const [popupControlComponentElement, setPopupControlComponentElement] = useState(null);
  const isNewlyCreatedOkr = !okrId || isTagEditionInSidePanelChildCreation;
  const toast = useToast();
  const { canCreateEditViewDeleteAssignCustomTags } = useCustomTagPermissions();
  const { type } = useSelector(kRDrawerSecondaryPanelSelector);
  const { visualViewportHeight: mobileViewportHeight } = useVisualViewportHeight();

  const isAddEditTagsPanelOpen = type === SECONDARY_PANEL_TYPES.ADD_EDIT_CUSTOM_TAGS;

  const { error: customTagsError, data: customTagsData } = useCustomTagsQuery();

  useEffect(() => {
    if (customTagsError) {
      toast({
        title: 'Failed to fetch tags',
        status: TOAST_STATUSES.ERROR,
      });
    }
  }, [customTagsError]);

  const controlComponentRef = useCallback((node) => {
    if (node !== null) {
      setPopupControlComponentElement(node);
      if (isMobileView()) {
        setPopupWidth(window.innerWidth);
        setOffset(node.getBoundingClientRect().top - document.body.scrollHeight); // Aligning bottom of popup to the bottom of viewport on mobile screens
        return;
      }
      setPopupWidth(node.getBoundingClientRect().width);
    }
  }, []);

  const handleResize = useCallback(() => {
    if (isMobileView() && popupControlComponentElement) {
      setPopupWidth(window.innerWidth);
      setOffset(popupControlComponentElement.getBoundingClientRect().top - document.body.scrollHeight); // Aligning bottom of popup to the bottom of viewport on mobile screens
    }
  }, [popupControlComponentElement]);

  const handleResizeDebounced = useMemo(() => debounce(handleResize, 250), [handleResize]);

  useEffect(() => {
    window.addEventListener('resize', handleResizeDebounced);

    return () => {
      window.removeEventListener('resize', handleResizeDebounced);
    };
  }, [handleResizeDebounced]);

  const handleTagsSave = (newTags) => {
    if (isNewlyCreatedOkr || isSendUpdatedTagsToParent) {
      onChange(newTags);
    }
    onClose();
  };

  const handleTagClick = (event) => {
    if (isReadOnly) {
      return;
    }
    if (event) {
      event.persist();
      // model is opened on click of tag, but if user clicks on cross svg icon on tag, we are overriding the event.
      if (event.target.tagName === 'svg' || event.target.tagName === 'path') {
        return;
      }
    }

    onToggle();
  };

  const getPlacementPosition = () => {
    // size based on height of popup *2 + height tag input
    if (isMobileView()) {
      return 'top';
    }
    const MIN_SIZE_TO_ACCOMMODATE_TOP_POSITION = 972;
    return window.innerHeight > MIN_SIZE_TO_ACCOMMODATE_TOP_POSITION ? 'top' : 'auto';
  };

  const mobileViewProps = isMobileView()
    ? {
        position: 'fixed',
        width: popupWidth,
        minW: popupWidth,
        top: 0,
        height: mobileViewportHeight,
      }
    : {};

  return (
    <Box {...containerProps}>
      <Popover
        flip
        returnFocusOnClose={false}
        isOpen={isOpen}
        onClose={() => {
          onClose();
        }}
        padding="0px"
        placement={getPlacementPosition()}
        gutter={popupOffset}
      >
        <PopoverTrigger>
          <ControlComponent
            onToggle={handleTagClick}
            isOpen={isOpen || isAddEditTagsPanelOpen}
            tags={tags}
            ref={controlComponentRef}
            isReadOnly={isReadOnly}
            okrId={okrId}
            onChange={onChange}
            handleTagsSave={handleTagsSave}
            customTagsData={customTagsData}
            tagNameLengthLimit={tagNameLengthLimit}
          />
        </PopoverTrigger>
        <PopoverContent
          maxW={`${popupWidth}px`}
          width={`${popupWidth}px`}
          padding="0px"
          boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.08) !important"
          {...mobileViewProps}
        >
          <PopoverBody overflowY="auto" padding="0px" height={isMobileView() ? '100%' : 'auto'}>
            <TagSearchAndSelect
              curSelectedTags={tags}
              isOpen={isOpen}
              onClose={() => {
                onClose();
              }}
              onSave={handleTagsSave}
              canCreateEditViewDeleteAssignCustomTags={canCreateEditViewDeleteAssignCustomTags}
              okrId={okrId}
              showNewTagCreation={showNewTagCreation}
              isTagEditionAsChild={isTagEditionAsChild}
              isTagEditionInSidePanelChildCreation={isTagEditionInSidePanelChildCreation}
              isSendUpdatedTagsToParent={isSendUpdatedTagsToParent}
              isNewlyCreatedOkr={isNewlyCreatedOkr}
            />
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
